import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import { ArrowBack, Email } from "@mui/icons-material";
import { Alert, Box, Button, CircularProgress, Link, Stack, TextField, Typography } from "@mui/material";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import type { Language } from "../api/langConfig";
import { langContext, termsUrl } from "../api/Lang";
import { auth, functions } from "../api/firebase";
import { authErrorMessage } from "./authErrors";
import { useLoginStrings } from "./login-strings";
import "./login.css";

type LoginStep =
    | "providers"
    | "emailEntry"
    | "signIn"
    | "signUp"
    | "forgotPassword"
    | "resetPassword"
    | "actionSuccess";

type SuccessKind = "resetEmailSent" | "passwordChanged" | "emailVerified";

type AuthUrlParams = {
    mode: string | null;
    oobCode: string | null;
};

function getAuthUrlParams(): AuthUrlParams {
    const params = new URLSearchParams(window.location.search);
    return {
        mode: params.get("mode"),
        oobCode: params.get("oobCode"),
    };
}

function clearAuthUrlParams(): void {
    const url = new URL(window.location.href);
    url.searchParams.delete("mode");
    url.searchParams.delete("oobCode");
    url.searchParams.delete("apiKey");
    url.searchParams.delete("lang");
    url.searchParams.delete("continueUrl");
    window.history.replaceState({}, document.title, url.pathname + url.hash);
}

async function populateEmailVerificationForGoogle(): Promise<void> {
    const populateEmailVerification = functions.httpsCallable("populateEmailVerificationForExistingUsers");
    await populateEmailVerification();
}

const GOOGLE_ICON_URL = "https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg";

function GoogleIcon() {
    return (
        <span className="login-form-btn-icon">
            <img src={GOOGLE_ICON_URL} alt="" />
        </span>
    );
}

function TermsFooter({ lang, wide }: { lang: Language; wide?: boolean }) {
    const s = useLoginStrings();
    return (
        <Typography className={`login-form-tos${wide ? " login-form-tos-wide" : ""}`} component="p">
            {s.tosPrefix}{" "}
            <Link href={termsUrl("terms", lang)} target="_blank" rel="noopener noreferrer">
                {s.tosLink}
            </Link>{" "}
            {s.tosAnd}{" "}
            <Link href={termsUrl("privacy", lang)} target="_blank" rel="noopener noreferrer">
                {s.privacyLink}
            </Link>.
        </Typography>
    );
}

export default function LoginForm() {
    const [lang] = useContext(langContext);
    const s = useLoginStrings();
    const [step, setStep] = useState<LoginStep>("providers");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);
    const [successKind, setSuccessKind] = useState<SuccessKind | null>(null);
    const [resetOobCode, setResetOobCode] = useState<string | null>(null);
    const urlHandledRef = useRef(false);

    const setAuthLanguage = useCallback(() => {
        auth.languageCode = lang;
    }, [lang]);

    const handleAuthError = useCallback((err: unknown) => {
        const code = (err as { code?: string })?.code;
        setError(authErrorMessage(code, s));
    }, [s]);

    const handleGoogleSignIn = async () => {
        setError("");
        setGoogleLoading(true);
        try {
            const provider = new firebase.auth.GoogleAuthProvider();
            const result = await auth.signInWithPopup(provider);
            if (result?.additionalUserInfo?.providerId === "google.com") {
                populateEmailVerificationForGoogle().catch((populateError: unknown) => {
                    console.error("Error populating email verification:", populateError);
                });
            }
        } catch (err) {
            handleAuthError(err);
        } finally {
            setGoogleLoading(false);
        }
    };

    const handleEmailNext = async () => {
        const trimmedEmail = email.trim();
        if (!trimmedEmail) {
            setError(s.authInvalidEmail);
            return;
        }
        setError("");
        setLoading(true);
        setAuthLanguage();
        try {
            const methods = await auth.fetchSignInMethodsForEmail(trimmedEmail);
            if (methods.includes(firebase.auth.EmailAuthProvider.EMAIL_PASSWORD_SIGN_IN_METHOD)) {
                setStep("signIn");
            } else if (methods.includes("google.com")) {
                setError(s.authAccountExistsDifferentProvider);
            } else {
                setStep("signUp");
            }
        } catch (err) {
            handleAuthError(err);
        } finally {
            setLoading(false);
        }
    };

    const handleSignIn = async () => {
        if (!password) {
            return;
        }
        setError("");
        setLoading(true);
        setAuthLanguage();
        try {
            await auth.signInWithEmailAndPassword(email.trim(), password);
        } catch (err) {
            handleAuthError(err);
        } finally {
            setLoading(false);
        }
    };

    const handleSignUp = async () => {
        if (password !== confirmPassword) {
            setError(s.passwordsDoNotMatch);
            return;
        }
        setError("");
        setLoading(true);
        setAuthLanguage();
        try {
            await auth.createUserWithEmailAndPassword(email.trim(), password);
        } catch (err) {
            handleAuthError(err);
        } finally {
            setLoading(false);
        }
    };

    const handleForgotPassword = async () => {
        const trimmedEmail = email.trim();
        if (!trimmedEmail) {
            setError(s.authInvalidEmail);
            return;
        }
        setError("");
        setLoading(true);
        setAuthLanguage();
        try {
            await auth.sendPasswordResetEmail(trimmedEmail, {
                url: window.location.origin + window.location.pathname,
            });
            setSuccessKind("resetEmailSent");
            setStep("actionSuccess");
        } catch (err) {
            handleAuthError(err);
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async () => {
        if (!resetOobCode) {
            return;
        }
        if (password !== confirmPassword) {
            setError(s.passwordsDoNotMatch);
            return;
        }
        setError("");
        setLoading(true);
        try {
            await auth.confirmPasswordReset(resetOobCode, password);
            clearAuthUrlParams();
            setSuccessKind("passwordChanged");
            setStep("actionSuccess");
            setPassword("");
            setConfirmPassword("");
        } catch (err) {
            handleAuthError(err);
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyEmail = useCallback(async (oobCode: string) => {
        setLoading(true);
        setError("");
        try {
            await auth.applyActionCode(oobCode);
            clearAuthUrlParams();
            setSuccessKind("emailVerified");
            setStep("actionSuccess");
        } catch (err) {
            handleAuthError(err);
            setStep("providers");
        } finally {
            setLoading(false);
        }
    }, [handleAuthError]);

    useEffect(() => {
        if (urlHandledRef.current) {
            return;
        }
        const { mode, oobCode } = getAuthUrlParams();
        if (!mode || !oobCode) {
            return;
        }
        urlHandledRef.current = true;
        if (mode === "resetPassword") {
            setResetOobCode(oobCode);
            setLoading(true);
            auth.verifyPasswordResetCode(oobCode)
                .then((verifiedEmail) => {
                    setEmail(verifiedEmail);
                    setStep("resetPassword");
                })
                .catch((err) => {
                    handleAuthError(err);
                    setStep("providers");
                })
                .finally(() => setLoading(false));
        } else if (mode === "verifyEmail") {
            handleVerifyEmail(oobCode);
        }
    }, [handleAuthError, handleVerifyEmail]);

    const goBack = () => {
        setError("");
        setPassword("");
        setConfirmPassword("");
        if (step === "signIn" || step === "signUp") {
            setStep("emailEntry");
        } else if (step === "emailEntry" || step === "forgotPassword") {
            setStep("providers");
        }
    };

    const successMessage = successKind === "resetEmailSent"
        ? s.resetEmailSent
        : successKind === "passwordChanged"
            ? s.passwordChanged
            : successKind === "emailVerified"
                ? s.emailVerified
                : "";

    return (
        <Box className="login-form-card">
            <div className="login-form-inner">
                {loading && step !== "providers" && step !== "emailEntry" && (
                    <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                        <CircularProgress size={28} />
                    </Box>
                )}

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
                        {error}
                    </Alert>
                )}

                {step === "actionSuccess" && (
                    <Stack spacing={2}>
                        <Alert severity="success">{successMessage}</Alert>
                        <Button variant="contained" onClick={() => { setStep("providers"); setSuccessKind(null); }}>
                            {s.signIn}
                        </Button>
                    </Stack>
                )}

                {step === "providers" && (
                    <>
                        <div className="login-form-providers">
                            <button
                                type="button"
                                className="login-form-btn login-form-btn-google"
                                disabled={googleLoading || loading}
                                onClick={handleGoogleSignIn}
                            >
                                {googleLoading ? (
                                    <span className="login-form-btn-icon">
                                        <CircularProgress size={18} />
                                    </span>
                                ) : (
                                    <GoogleIcon />
                                )}
                                <span className="login-form-btn-label">{s.signInWithGoogle}</span>
                            </button>
                            <button
                                type="button"
                                className="login-form-btn login-form-btn-email"
                                disabled={googleLoading || loading}
                                onClick={() => { setStep("emailEntry"); setError(""); }}
                            >
                                <span className="login-form-btn-icon">
                                    <Email sx={{ fontSize: 20, color: "inherit" }} />
                                </span>
                                <span className="login-form-btn-label">{s.continueWithEmail}</span>
                            </button>
                        </div>
                        <TermsFooter lang={lang} wide />
                    </>
                )}

                {step === "emailEntry" && (
                        <Stack spacing={2}>
                            <Button className="login-form-back" startIcon={<ArrowBack />} onClick={goBack}>
                                {s.back}
                            </Button>
                            <TextField
                                label={s.emailLabel}
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                fullWidth
                                autoComplete="username"
                                autoFocus
                                disabled={loading}
                                onKeyDown={(e) => e.key === "Enter" && handleEmailNext()}
                            />
                            <Button variant="contained" fullWidth disabled={loading} onClick={handleEmailNext}>
                                {loading ? <CircularProgress size={22} color="inherit" /> : s.next}
                            </Button>
                            <TermsFooter lang={lang} wide />
                        </Stack>
                    )}

                    {step === "signIn" && (
                        <Stack spacing={2}>
                            <Button className="login-form-back" startIcon={<ArrowBack />} onClick={goBack}>
                                {s.back}
                            </Button>
                            <TextField
                                label={s.emailLabel}
                                type="email"
                                value={email}
                                fullWidth
                                disabled
                                slotProps={{ htmlInput: { readOnly: true } }}
                            />
                            <TextField
                                label={s.passwordLabel}
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                fullWidth
                                autoComplete="current-password"
                                autoFocus
                                disabled={loading}
                                onKeyDown={(e) => e.key === "Enter" && handleSignIn()}
                            />
                            <Button variant="text" onClick={() => { setStep("forgotPassword"); setError(""); }} sx={{ alignSelf: "flex-start", textTransform: "none" }}>
                                {s.forgotPassword}
                            </Button>
                            <Button variant="contained" fullWidth disabled={loading || !password} onClick={handleSignIn}>
                                {loading ? <CircularProgress size={22} color="inherit" /> : s.signIn}
                            </Button>
                            <TermsFooter lang={lang} wide />
                        </Stack>
                    )}

                    {step === "signUp" && (
                        <Stack spacing={2}>
                            <Button className="login-form-back" startIcon={<ArrowBack />} onClick={goBack}>
                                {s.back}
                            </Button>
                            <TextField
                                label={s.emailLabel}
                                type="email"
                                value={email}
                                fullWidth
                                disabled
                                slotProps={{ htmlInput: { readOnly: true } }}
                            />
                            <TextField
                                label={s.passwordLabel}
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                fullWidth
                                autoComplete="new-password"
                                autoFocus
                                disabled={loading}
                            />
                            <TextField
                                label={s.confirmPasswordLabel}
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                fullWidth
                                autoComplete="new-password"
                                disabled={loading}
                                onKeyDown={(e) => e.key === "Enter" && handleSignUp()}
                            />
                            <Button variant="contained" fullWidth disabled={loading || !password || !confirmPassword} onClick={handleSignUp}>
                                {loading ? <CircularProgress size={22} color="inherit" /> : s.createAccount}
                            </Button>
                            <TermsFooter lang={lang} wide />
                        </Stack>
                    )}

                    {step === "forgotPassword" && (
                        <Stack spacing={2}>
                            <Button className="login-form-back" startIcon={<ArrowBack />} onClick={goBack}>
                                {s.back}
                            </Button>
                            <TextField
                                label={s.emailLabel}
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                fullWidth
                                autoComplete="username"
                                autoFocus
                                disabled={loading}
                                onKeyDown={(e) => e.key === "Enter" && handleForgotPassword()}
                            />
                            <Button variant="contained" fullWidth disabled={loading} onClick={handleForgotPassword}>
                                {loading ? <CircularProgress size={22} color="inherit" /> : s.sendResetLink}
                            </Button>
                        </Stack>
                    )}

                    {step === "resetPassword" && (
                        <Stack spacing={2}>
                            <TextField
                                label={s.emailLabel}
                                type="email"
                                value={email}
                                fullWidth
                                disabled
                                slotProps={{ htmlInput: { readOnly: true } }}
                            />
                            <TextField
                                label={s.passwordLabel}
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                fullWidth
                                autoComplete="new-password"
                                autoFocus
                                disabled={loading}
                            />
                            <TextField
                                label={s.confirmPasswordLabel}
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                fullWidth
                                autoComplete="new-password"
                                disabled={loading}
                                onKeyDown={(e) => e.key === "Enter" && handleResetPassword()}
                            />
                            <Button variant="contained" fullWidth disabled={loading || !password || !confirmPassword} onClick={handleResetPassword}>
                                {loading ? <CircularProgress size={22} color="inherit" /> : s.saveNewPassword}
                            </Button>
                        </Stack>
                    )}
            </div>
        </Box>
    );
}
